import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { InboxAutomatorMailbox, InboxAutomatorService, InboxAutomatorState } from './inbox-automator.service';

export interface InboxAutomatorModalOptions {
  email: string;
  provider?: string;
  /** Opened straight after the mailbox was connected: lead with the confirmation. */
  justConnected?: boolean;
  /** Reopened after a plan was bought in the upgrade modal: Activate is one click away. */
  justUpgraded?: boolean;
}

/**
 * 'upgrade' is internal: the modal closes itself and InboxAutomatorModalService shows the
 * upgrade modal in its place -- two dialogs stacked read as clutter -- then brings this back.
 */
export type InboxAutomatorModalResult = 'activated' | 'deactivated' | 'upgrade' | null;

/**
 * SD-1229: Inbox Automator for one mailbox.
 *
 * Connecting Gmail or Outlook syncs its mail on every plan -- that part is always on and the
 * modal says so first. Inbox Automator is the paid step beyond it. The modal offers it in the
 * one form that fits the caller: Activate (their plan includes it), Upgrade (it does not, and
 * they may buy), or Ask an admin (their plan includes it but their role cannot switch it on).
 * Upgrading opens the upgrade modal on top and comes back here with Activate one click away.
 *
 * Switching it on records the choice. The automation itself still runs through a workflow
 * set up with the customer, which the copy is careful not to over-promise.
 */
@Component({
  selector: 'app-inbox-automator-modal',
  templateUrl: './inbox-automator-modal.component.html',
  styleUrls: ['./inbox-automator-modal.component.scss']
})
export class InboxAutomatorModalComponent implements OnInit, OnDestroy {
  @Input() options: InboxAutomatorModalOptions;

  loading = true;
  busy = false;
  justUpgraded = false;
  errorMessage = '';
  state: InboxAutomatorState | null = null;
  mailbox: InboxAutomatorMailbox | null = null;
  result: InboxAutomatorModalResult = null;

  private organizationId: string;
  private destroy$ = new Subject<void>();

  constructor(
    public activeModal: NgbActiveModal,
    private inboxAutomator: InboxAutomatorService,
    private organizationService: OrganizationService
  ) { }

  ngOnInit(): void {
    this.organizationId = this.organizationService.selectedOrganization;
    this.justUpgraded = !!this.options?.justUpgraded;
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get email(): string {
    return this.mailbox?.email || (this.options?.email || '').trim().toLowerCase();
  }

  get provider(): string {
    return this.mailbox?.provider || this.options?.provider || 'Gmail';
  }

  get providerIcon(): string {
    return /outlook/i.test(this.provider) ? 'assets/img/email/outlook.png' : 'assets/img/email/gmail.png';
  }

  /** Which call to action the caller gets. */
  get mode(): 'active' | 'activate' | 'upgrade' | 'ask-admin' | 'unknown' {
    if (!this.state || !this.mailbox) { return 'unknown'; }
    if (this.mailbox.isActive) { return 'active'; }
    if (this.state.entitled) { return 'activate'; }
    return this.state.planIncludes ? 'ask-admin' : 'upgrade';
  }

  load(): void {
    this.loading = true;
    this.errorMessage = '';
    this.inboxAutomator.invalidate(this.organizationId);
    this.inboxAutomator.state(this.organizationId)
      .pipe(take(1), takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe(state => {
        this.state = state;
        this.mailbox = InboxAutomatorService.find(state, this.options?.email);
      }, () => this.errorMessage = 'We couldn’t load Inbox Automator for this mailbox.');
  }

  activate(): void {
    this.busy = true;
    this.errorMessage = '';
    this.inboxAutomator.activate(this.organizationId, this.email)
      .pipe(take(1), takeUntil(this.destroy$), finalize(() => this.busy = false))
      .subscribe(() => {
        this.result = 'activated';
        this.load();
      }, err => {
        this.errorMessage = err?.status === 403
          ? 'Your plan doesn’t include Inbox Automator.'
          : 'We couldn’t switch Inbox Automator on. Please try again.';
        this.load();
      });
  }

  deactivate(): void {
    this.busy = true;
    this.errorMessage = '';
    this.inboxAutomator.deactivate(this.organizationId, this.email)
      .pipe(take(1), takeUntil(this.destroy$), finalize(() => this.busy = false))
      .subscribe(() => {
        this.result = 'deactivated';
        this.load();
      }, () => this.errorMessage = 'We couldn’t switch Inbox Automator off. Please try again.');
  }

  /** Hands over to the upgrade modal; InboxAutomatorModalService reopens this afterwards. */
  upgrade(): void {
    this.activeModal.close('upgrade');
  }

  close(): void {
    this.activeModal.close(this.result);
  }
}
